import React, { useState, useCallback } from 'react';
import classNames from 'classnames';

import { Modal, ModalContent, ModalButtonGroup } from 'js/design_system/Modal';
import { Button } from 'js/design_system/Button';
import { TextButton } from 'js/design_system/TextButton';
import { Heading } from 'js/design_system/Heading';
import { Body } from 'js/design_system/Body';
import { Checkbox } from 'js/design_system/forms/Checkbox';

import 'app/assets/stylesheets/paper_claiming/paper_claiming_modal.scss';

export type PaperToClaim = {
  id: number | string;
  title: string;
  authorName: string;
  openalexAuthorId?: string;
};

export type PaperClaimingOnsiteModalProps = {
  /**
   * Whether the modal is open
   */
  open: boolean;
  /**
   * Callback when the modal is closed
   */
  onClose: () => void;
  /**
   * Papers available to claim
   */
  papers: PaperToClaim[];
  /**
   * Callback when papers are claimed
   */
  onClaimPapers: (paperIds: (number | string)[]) => void;
  /**
   * Callback when user indicates they didn't author these papers
   */
  onDidNotAuthor?: () => void;
  /**
   * Whether the claim action is loading
   */
  isLoading?: boolean;
};

/**
 * A simple modal for claiming research papers on Home and Profile pages.
 * Shows papers with checkboxes and a submit button.
 *
 * Based on the Figma design for paper claiming at login (PMK 2025).
 */
export const PaperClaimingOnsiteModal: React.FC<
  PaperClaimingOnsiteModalProps
> = ({
  open,
  onClose,
  papers,
  onClaimPapers,
  onDidNotAuthor,
  isLoading = false,
}) => {
  const [selectedPaperIds, setSelectedPaperIds] = useState<
    Set<number | string>
  >(new Set());

  const togglePaper = useCallback((paperId: number | string) => {
    setSelectedPaperIds((prev) => {
      const next = new Set(prev);
      if (next.has(paperId)) {
        next.delete(paperId);
      } else {
        next.add(paperId);
      }
      return next;
    });
  }, []);

  const handleClaimPapers = useCallback(() => {
    onClaimPapers(Array.from(selectedPaperIds));
  }, [onClaimPapers, selectedPaperIds]);

  const handleClose = useCallback(() => {
    setSelectedPaperIds(new Set());
    onClose();
  }, [onClose]);

  const hasSelectedPapers = selectedPaperIds.size > 0;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      showHeader={false}
      className="paper-claiming-modal"
    >
      <ModalContent className="paper-claiming-modal__content">
        {/* Header */}
        <div className="paper-claiming-modal__header">
          <Heading size="md" as="h2">
            Did you author any of these papers?
          </Heading>
          <Body size="md" className="paper-claiming-modal__description">
            Claim your work to track your citations and academic impact with
            precision.
          </Body>
        </div>

        {/* Papers List */}
        <div className="paper-claiming-modal__papers">
          {papers.map((paper, index) => (
            <React.Fragment key={paper.id}>
              <div
                className={classNames('paper-claiming-modal__paper-row', {
                  'paper-claiming-modal__paper-row--selected':
                    selectedPaperIds.has(paper.id),
                })}
              >
                <Checkbox
                  checked={selectedPaperIds.has(paper.id)}
                  onChange={() => togglePaper(paper.id)}
                  aria-label={`Select paper: ${paper.title}`}
                />
                <div
                  role="button"
                  tabIndex={0}
                  className="paper-claiming-modal__paper-info"
                  onClick={() => togglePaper(paper.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      togglePaper(paper.id);
                    }
                  }}
                >
                  <Body size="md" className="paper-claiming-modal__paper-title">
                    {paper.title}
                  </Body>
                  <Body
                    size="sm"
                    className="paper-claiming-modal__paper-author"
                  >
                    by {paper.authorName}
                  </Body>
                </div>
              </div>
              {index < papers.length - 1 && (
                <div className="paper-claiming-modal__divider" />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Disclaimer */}
        <Body size="sm" className="paper-claiming-modal__disclaimer">
          Adding papers will add them to your Academia.edu profile | Powered by
          OpenAlex
        </Body>

        {/* Button Group */}
        <ModalButtonGroup>
          {onDidNotAuthor && (
            <TextButton onClick={onDidNotAuthor}>
              I did not author any of these papers
            </TextButton>
          )}
          <Button
            onClick={handleClaimPapers}
            disabled={!hasSelectedPapers || isLoading}
          >
            {isLoading ? 'Submitting...' : 'Submit'}
          </Button>
        </ModalButtonGroup>
      </ModalContent>
    </Modal>
  );
};

export default PaperClaimingOnsiteModal;
